import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import EmailTemplateForm from "../emailTemplateForm";
import { emailTemplate } from "@/constants/translation/emailTemplate.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import EmailTemplateValidationClass from "@/helpers/validations/emailTemplate";
import { messages } from "@/constants/translation/common.translation";
import { EmailTemplateService } from "@/api/emailTemplateService";     
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";
        
        
/**
 * Component for editing an email template.
 * React component for editing an existing email template.
 * 
 * This component fetches an email template by ID, allows modifications, and updates it upon form submission.
 * @returns {JSX.Element} The rendered EditEmailTemplate component.
 */
const EditEmailTemplate = () => {
  const { id } = useParams(); // Extract "id" parameter from URL
  const navigate = useNavigate();
  const t = useTranslationContext();
  
  
  
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [emailTemplateData, setEmailTemplateData] = useState(null);

  useEffect(() => {
    // Fetch languages on component mount
    const fetchLanguages = async () => {
      
      try {
        
        const languagesResponse = await TranslationService.getLanguages();
        
        
        const languagesData = languagesResponse.data || languagesResponse;
        let languagesList = [];
        
        if (Array.isArray(languagesData)) {
          languagesList = languagesData;
        } else if (languagesData && typeof languagesData === constants.dataTypes.object) {
          languagesList = Object.values(languagesData).map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code,
            isDefault: lang.isDefault,
            displayName: lang.name
          }));
          
          // Sort languages: English first, then others alphabetically
          languagesList.sort((a, b) => {
            if (a.name.toLowerCase() === constants.defaultLanguage) return -1;
            if (b.name.toLowerCase() === constants.defaultLanguage) return 1;
            return a.name.localeCompare(b.name);
          });
        }
        
        
        setLanguages(languagesList);
      } catch (error) {
        console.error("Fetch languages error:", error);
        toast.error(t(messages.toast.translation.languagesFetchFailed));
      }
    };
    
    fetchLanguages();
  }, []);

  useEffect(() => {
    // Fetch email template data when component mounts and languages are loaded
    const fetchEmailTemplate = async () => {
      
      if (!id || languages.length === 0) {
        
        return;
      }
      
      setIsDataLoading(true);
      try {
        
    
        const response = await EmailTemplateService.get(id);
        
        setEmailTemplateData(response.data);
      } catch (error) {
        console.error("Fetch email template error:", error);
        toast.error(t(messages.toast.emailTemplate.dataFetchFailed));
      } finally {
        setIsDataLoading(false);
      }
    };
    
    fetchEmailTemplate();
  }, [id, languages, t]);

  const handleUpdateEmailTemplate = async (values) => {
    
    
    setIsLoading(true);
    try {
      
      
      
      // Transform data structure to match API expectations
      const apiData = {
        uuid: id, // Use uuid as main identifier
        slug: values.slug,
        parameter: values.parameter,
        templates: values.email_templates.map(template => ({
          uuid: template.uuid || null,
          language_id: template.language_id, // Use language_id (snake_case) for API
          title: template.title,
          subject: template.subject,
          content: template.content
        }))
      };
      
      
      
      const response = await EmailTemplateService.update(apiData);
      
      toast.success(response.message || t(messages.toast.emailTemplate.updated));
      navigate(masters.emailTemplate.list);
    } catch (error) {
      console.error("Update email template error:", error);
      toast.error(t(messages.toast.emailTemplate.updateFailed));
    } finally {
      setIsLoading(false);
    }
  };

  // Initial values for the email template form
  const initialValues = useMemo(() => {
    
    
    
    
    

    
    // Default structure with languages (like add page)
    const defaultValues = {
      id: id,
      slug: "",
      parameter: "",
      email_templates: languages.map((lang) => ({
        language: lang.name,
        language_id: lang.id,
        title: "",
        subject: "",
        content: "",
        uuid: null
      }))
    };
    
    // If email template data is loaded, update the default values
    if (emailTemplateData && emailTemplateData.templates) {
      
      
      const updatedValues = {
        ...defaultValues,
        slug: emailTemplateData.slug,
        parameter: emailTemplateData.parameter,
        email_templates: defaultValues.email_templates.map((langTemplate) => {
          // Find corresponding template from API data using languageId (not language_id)
          const apiTemplate = emailTemplateData.templates.find(
            template => String(template.languageId) === String(langTemplate.language_id)
          );
          
          if (apiTemplate) {
            
            return {
              ...langTemplate,
              title: apiTemplate.title,
              subject: apiTemplate.subject,
              content: apiTemplate.content,
              uuid: apiTemplate.uuid
            };
          }
          
          return langTemplate;
        })
      };
      
      
      return updatedValues;
    }
    
    
    return defaultValues;
  }, [languages, emailTemplateData, id]);

  // Validation schema for the email template form
  const validationSchema = useMemo(
    () => EmailTemplateValidationClass.emailTemplateValidation(),
    []
  );

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>Loading email template data...</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(emailTemplate.edit.main_heading)}</h1>
        <EmailTemplateForm
          initialValues={initialValues}
          onSubmit={handleUpdateEmailTemplate}
          emailTemplatedLoaded={isLoading}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

// Static data that would have been passed via getServerSideProps
EditEmailTemplate.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.emailTemplate?.list,
    pathname: masters.emailTemplate.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.emailTemplate?.edit,
    pathname: '',
    active: true,
  },
];

export default EditEmailTemplate;
